import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { LockedUi } from "components/LockedUi/LockedUi";
import { Padlock } from "components/icons/Padlock";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { INVOICE_RECURRING_TOGGLED } from "constants/analyticsEvents";
import { RECURRING_INVOICE } from "constants/subscriptionPermissionFeatures";
import { useFormikContext } from "formik";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { RecurringInvoiceSetting } from "pages/Books/Invoicing/Recurring/RecurringInvoiceSetting";
import { ReactNode, useEffect, useState } from "react";
import { useWindowSize } from "hooks/useWindowSize";
import { EditPencil } from "components/icons/EditPencil";
import { useParams } from "react-router-dom";
import {
  useGetEntityInvoiceQuery,
  useUpdateInvoiceRecurringMutation,
} from "store/apis/invoices";
import { Invoice } from "types/Models/invoice";
import { formatDate } from "utils/formatDate";
import { PadlockIcon } from "components/icons/PadlockIcon";

export const InvoiceConfiguration = () => {
  const { invoiceId } = useParams<{
    invoiceId: string;
  }>();
  const [configureRecurring, setConfigureRecurring] = useState(false);
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const [updateInvoiceRecurring] = useUpdateInvoiceRecurringMutation();
  const invoiceRecurringModal = useModal();
  const { alertToast, successToast } = useToast();
  const { trackEvent } = useAnalytics();

  const { data: invoice } = useGetEntityInvoiceQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      invoiceId,
    },
    { skip: !group?.uuid || !entityId }
  );

  const { isMobile } = useWindowSize();
  const { values } = useFormikContext<Partial<Invoice>>();

  const { validateForm } = useFormikContext();

  useEffect(() => {
    setConfigureRecurring(
      Boolean(invoice?.invoice_settings?.is_recurring_enabled)
    );
  }, [invoice?.invoice_settings]);

  const onRecurringInvoiceAdd = async () => {
    if (group?.uuid && entityId && invoice?.invoice_settings?.uuid) {
      try {
        const payload = {
          is_recurring_enabled: false,
        };
        await updateInvoiceRecurring({
          entityId,
          groupId: group.uuid,
          invoiceId,
          payload,
          invoiceSettingId: invoice?.invoice_settings.uuid,
        }).unwrap();
        successToast({
          message: "Invoice recurring disabled",
        });
      } catch (error: any) {
        alertToast({ message: error?.data?.error?.message });
      }
    }
  };

  const handleToggleUpdate = (value: boolean) => {
    setConfigureRecurring(value);
    if (value) invoiceRecurringModal.open();
    else if (invoice?.invoice_settings?.is_recurring_enabled) {
      onRecurringInvoiceAdd();
    }
  };

  if (!invoice) {
    return null;
  }

  const handleRecurringSettingClose = () => {
    invoiceRecurringModal.close();
    if (!invoice.invoice_settings?.is_recurring_enabled) {
      setConfigureRecurring(false);
    }
  };

  const invoiceItems = invoice.invoice_items || [];

  const samePeriod = invoiceItems.some((item) => item.billing_period_enabled)
    ? invoiceItems.every(
        (item) =>
          item.billing_period_end_date ===
            invoiceItems[0].billing_period_end_date &&
          item.billing_period_start_date ===
            invoiceItems[0].billing_period_start_date &&
          item.billing_period_enabled === true
      )
    : true;

  const disableRecurring = !samePeriod;

  const handleRecurringSwitchChange = async (value: boolean) => {
    trackEvent(INVOICE_RECURRING_TOGGLED);
    const errors = await validateForm();
    if (Object.entries(errors).length > 0) {
      return alertToast({
        message: "Please fill all the required fields.",
      });
    }
    handleToggleUpdate(value);
  };

  const recurringLabel = (
    <span className="t-text-body t-text-text-100 sm:t-text-body-sm">
      Make invoice recurring
    </span>
  );

  const renderRecurringSwitch = (label: ReactNode) => (
    <PermissionBasedUI
      align="end"
      side="bottom"
      feature={RECURRING_INVOICE}
      blockedUI={
        <span className="t-flex t-items-center t-gap-2">
          <Switch
            size="small"
            aria-label="Make invoice recurring"
            disabled={!invoice?.invoice_settings?.is_recurring_enabled}
            name="recurringInvoice"
            onCheckedChange={handleToggleUpdate}
            checked={configureRecurring}
          />
          {label}
          <PadlockIcon />
        </span>
      }
    >
      <span className="t-flex t-items-center t-gap-2">
        <ConditionalToolTip
          align="end"
          side="bottom"
          condition={
            disableRecurring && (
              <span>
                Recurring invoices are only supported <br /> when all line items
                have the same billing <br /> period. To enable recurring, adjust
                the <br /> billing periods so they match.
              </span>
            )
          }
        >
          <span className="t-flex">
            <Switch
              aria-label="Make invoice recurring"
              checked={configureRecurring}
              disabled={disableRecurring || !hasWritePermission}
              size="small"
              name="recurringInvoice"
              onCheckedChange={handleRecurringSwitchChange}
            />
          </span>
        </ConditionalToolTip>
        {label}
      </span>
    </PermissionBasedUI>
  );

  const recurringModal = invoiceRecurringModal.isOpen && entityId && (
    <RecurringInvoiceSetting
      isOpen={invoiceRecurringModal.isOpen}
      close={handleRecurringSettingClose}
      invoice={invoice}
      entityId={entityId}
    />
  );

  if (isMobile) {
    return (
      <div className="t-flex t-flex-col t-rounded-lg t-bg-surface-lighter-grey t-px-3 t-py-2">
        <div className="t-flex t-items-center t-justify-between">
          {recurringLabel}
          {renderRecurringSwitch(null)}
        </div>
        {invoice.invoice_settings?.next_recurring_date && (
          <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30">
            Next invoice will be dated{" "}
            {formatDate(invoice.invoice_settings.next_recurring_date)}
            {invoice.invoice_settings.is_recurring_enabled && (
              <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
                <Button
                  type="button"
                  customType="ghost_icon"
                  size="xsmall"
                  aria-label="Configure recurring invoice"
                  onClick={invoiceRecurringModal.open}
                >
                  <EditPencil />
                </Button>
              </LockedUi>
            )}
          </div>
        )}
        {recurringModal}
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-items-end t-gap-1">
      <div className="t-flex t-gap-3 t-items-center">
        {invoice.invoice_settings?.is_recurring_enabled && (
          <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
            <Button
              type="button"
              customType="link"
              size="small"
              onClick={invoiceRecurringModal.open}
            >
              Configure
            </Button>
          </LockedUi>
        )}
        {renderRecurringSwitch(recurringLabel)}
      </div>
      {invoice.invoice_settings?.next_recurring_date && (
        <div className="t-text-body-sm t-text-text-30">
          Next invoice will be dated{" "}
          {formatDate(invoice.invoice_settings?.next_recurring_date)}
        </div>
      )}
      {invoiceRecurringModal.isOpen && entityId && (
        <RecurringInvoiceSetting
          isOpen={invoiceRecurringModal.isOpen}
          close={handleRecurringSettingClose}
          invoice={invoice}
          entityId={entityId}
        />
      )}
    </div>
  );
};
